import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Selector from '../../components/shared/Selector/Selector';
import { WizardProject, serviceEvaluations } from '../../services/service-evaluations';
import { noOptionsMessage, useServerSearch } from './useServerSearch';
import styles from './app-evaluation-wizard.module.scss';

type Props = {
  value: WizardProject | null;
  onChange: (project: WizardProject | null) => void;
};

type Option = { value: string; label: string };

const searchProjects = (q: string) => serviceEvaluations.getWizardProjects({ q });

const optionOf = (project: WizardProject): Option => ({
  value: project._id,
  label: project.number ? `${project.name} (${project.number})` : project.name,
});

const ProjectField = ({ value, onChange }: Props) => {
  const [t] = useTranslation('evaluations');
  const { results, status, input, onInputChange } = useServerSearch(searchProjects);
  const noMatch = t('wizard.context.noMatch', 'Inga projekt matchar');
  const NoOptionsMessage = useMemo(() => noOptionsMessage<Option>(noMatch), [noMatch]);

  return (
    <div className={styles.fieldStack}>
      <Selector<Option>
        isSearchable
        label={t('wizard.context.existing', 'Befintligt projekt')}
        placeholder={t('wizard.context.projectPlaceholder', 'Välj projekt')}
        options={results.map(optionOf)}
        value={value && optionOf(value)}
        onChange={option =>
          onChange(results.find(project => project._id === option?.value) ?? null)
        }
        onInputChange={onInputChange}
        getSearchString={() => input}
        components={{ NoOptionsMessage }}
        {...({
          isLoading: status === 'loading',
          loadingMessage: () => t('wizard.context.loading', 'Hämtar projekt…'),
        } as object)}
      />
      {status === 'failed' ? (
        <p className={styles.loadError}>
          {t('wizard.context.loadFailed', 'Projekten kunde inte hämtas.')}
        </p>
      ) : (
        <p className={styles.hint}>
          {t('wizard.context.projectHelp', 'Visar projekt du har tillgång till.')}
        </p>
      )}
    </div>
  );
};

export default ProjectField;
